/* eslint-disable max-lines */
import { takeLatest, put, select, call, debounce, all, fork } from 'redux-saga/effects';
import isEqual from 'lodash/isEqual';
import { readCookie, parseBoolean, getAPIConfig } from '@tcp/core/src/utils';
import {
  getFlipSwitchState,
  getUnbxdDRUrlState,
  getInitialSubCategoryToLoad,
  getSubCategoryToLoadOnScroll,
  getNumberOfProductsPerHLPRow,
  isShopThisStoreModeOn,
  getNumberOfProductsPerAPLPRow,
  getIsHplpFiltersEnabled,
  getIsHplpCmsSbcEnabled,
  getIsESpotPromoHplpEnabled,
  getIsL3AtL2InHPLPEnabled,
  getIsCleanUrlForSizeEnabled,
} from '@tcp/core/src/reduxStore/selectors/session.selectors';
import fetchPlpProducts from '@tcp/core/src/services/abstractors/categoryListing/categoryListing';
import { SEO_DATA } from '@tcp/core/src/reduxStore/constants';
import isEmpty from 'lodash/isEmpty';
import logger from '@tcp/core/src/utils/loggerInstance';
import { getModuleDynamic } from '@tcp/core/src/services/abstractors/common/moduleDynamic';
import HORIZONTAL_PLP_CONSTANTS from './HorizontalCategory.constants';
import {
  setHorizontalPlpLoading,
  setHorizontalCategoryProducts,
  setHlpCatsFetched,
  setHlpCID,
  clearHorizontalPageSEOData,
  setHplpFilters,
  setHplpAppliedFilters,
  setHplpAppliedSortId,
  setHplpInitialDataLoading,
} from './HorizontalCategory.actions';
import { getHlpCatgories, findEspotHplpPromo } from './HorizontalCategory.utils';
import {
  computedBreadCrumbs,
  getHplpLayoutData,
} from '../../CategoryListingPage/container/CategoryListingPage.selectors';
import {
  getHorizontalPlpFilters,
  getHorizontalPlpProducts,
  getInitialHplpValues,
} from './HorizontalCategory.selectors';
import {
  setLoadedInShopThisStore,
  setIsShopThisStoreHplpLoading,
} from '../../ShopThisStore/container/ShopThisStore.actions';
import { getStoreFilterData } from '../../ShopThisStore/container/ShopThisStore.utils';
import { getIsLoadedInShopThisStore } from '../../ShopThisStore/container/ShopThisStore.selectors';
import {
  loadCategoryPageSeoData,
  setIsProductLoading,
} from '../../CategoryListingPage/container/CategoryListingPage.actions';
import {
  extractFilters,
  getAppliedFilters,
  getFacetSwatchImgPath,
  getFiltersPayload,
  getIsL2Category,
  parseContentData,
} from '../../CategoryListingPage/container/CategoryListingPage.utils';
import { getFiltersAfterProcessing } from '../../../../../services/abstractors/productListing/processResponse';
import {
  fetchModulesAndLayout,
  fetchHplpModulesAndLayout,
} from '../../ProductListing/container/ProductListing.saga';
import { parseHPLPProductInfo } from '../../../../../services/abstractors/productListing/productParser';
import { loadLayoutData } from '../../../../../reduxStore/actions';
import { getESpotPlp } from '../../ProductListing/container/ProductListing.util';

const CURRENT_CATEGORY = new Map();

async function getESpotHplpPromo({ categoryInfo }) {
  let eSpotPlpPromoData = null;
  try {
    eSpotPlpPromoData = await findEspotHplpPromo({
      categoryInfo,
    });
  } catch (error) {
    logger.error({
      error: `Error in new plp HorizontalCategorySaga - getESpotHplpPromo catch - ${error}`,
      extraData: {
        componentName: 'HorizontalCategorySaga',
        methodName: 'getESpotHplpPromo',
        isServiceError: error?.isServiceError || false,
      },
    });
  }
  return eSpotPlpPromoData;
}

export function* fetchModuleDynamic({ espotContentId, id, typeName }) {
  let eSpotPlpPromoModuleData = null;
  try {
    eSpotPlpPromoModuleData = yield call(getModuleDynamic, espotContentId, id, typeName, true);
  } catch (error) {
    logger.error({
      error: `Error in new plp HorizontalCategorySaga - fetchModuleDynamic catch - ${error}`,
      extraData: {
        componentName: 'HorizontalCategorySaga',
        methodName: 'fetchModuleDynamic',
        isServiceError: error?.isServiceError || false,
      },
    });
  }
  return eSpotPlpPromoModuleData;
}

const getCategoryBasedOnSelectedGroupId = (selectedGroupId, availableSubCat) => {
  return availableSubCat?.filter((item) => item?.categoryInfo?.groupIdentifier === selectedGroupId);
};

const getUrlWithBopisChanges = (url) => {
  if (url?.includes('#redirect')) {
    return `${url
      ?.replace('#redirect', `?bopisStoreId=${getStoreFilterData()}&mode=shop-this-store`)
      ?.replace('/c?cid=', '')}`;
  }
  return `${url?.replace('/c?cid=', '')}?bopisStoreId=${getStoreFilterData()}&mode=shop-this-store`;
};

const updateShopThisStoreQueryParamUrl = (availableSubCat) => {
  return availableSubCat.map((item) => {
    return {
      ...item,
      categoryInfo: {
        ...item?.categoryInfo,
        url: item?.categoryInfo?.url
          ? getUrlWithBopisChanges(item?.categoryInfo?.url)
          : `${
              item?.categoryInfo?.seoToken
            }?bopisStoreId=${getStoreFilterData()}&mode=shop-this-store`,
      },
    };
  });
};

function* getLayoutModels(res, facetsResponse, apiConfig) {
  let { banner: { banners } = {} } = facetsResponse || {};
  if (!banners) {
    banners = res?.banner?.banners;
  }
  const contentData = banners && parseContentData(banners);
  const config = apiConfig || getAPIConfig();
  yield fork(fetchModulesAndLayout, contentData, config, true);
}

export function* getUnbxdXappConfigs() {
  const isFlipUnbxdDrUrl = readCookie('isFlipUnbxdDrUrl');
  const isFlipUrlSwitch = yield select(getFlipSwitchState);
  const drURLpath = yield select(getUnbxdDRUrlState);
  return {
    isFlipURL: isFlipUrlSwitch || parseBoolean(isFlipUnbxdDrUrl),
    drURLpath,
  };
}

export function* processHplpFacets(
  facets,
  numberOfProducts,
  filtersFromUrl,
  breadCrumbs,
  stats,
  appliedSortId
) {
  const isCleanUrlForSizeEnabled = yield select(getIsCleanUrlForSizeEnabled);
  const filtersMaps = getFiltersAfterProcessing(
    facets,
    numberOfProducts,
    getFacetSwatchImgPath,
    filtersFromUrl,
    breadCrumbs?.length === 1,
    false,
    stats,
    isCleanUrlForSizeEnabled
  );
  const appliedFilters = getAppliedFilters(filtersMaps, filtersFromUrl);
  yield put(setHplpFilters(filtersMaps));
  yield put(setHplpAppliedFilters(appliedFilters));
  yield put(setHplpAppliedSortId(appliedSortId));
}

export function* preFacetTasks(parentCategory, filtersFromUrl, pathName) {
  const filterMaps = yield select(getHorizontalPlpFilters);

  yield put(
    setHorizontalCategoryProducts({
      [parentCategory]: [{ categoryInfo: {}, products: [], numberOfProducts: 0 }],
    })
  );
  const appliedFilters = getAppliedFilters(filterMaps, filtersFromUrl, pathName);
  yield put(setHplpAppliedFilters(appliedFilters));
}

export function* fetchHorizontalCategoryFacets(payload) {
  try {
    const { categoryPathId, xappURLConfig, apiConfig, pathName, filters, parentCategory } = payload;
    const { facetsPayload, filtersFromUrl } = getFiltersPayload(pathName, filters);
    const breadCrumbs = yield select(computedBreadCrumbs);
    const isHplpCmsSbcEnabled = yield select(getIsHplpCmsSbcEnabled);
    const currentProducts = yield select(getHorizontalPlpProducts);
    if (!currentProducts[parentCategory])
      yield call(preFacetTasks, parentCategory, filtersFromUrl, pathName);
    const { sort: appliedSortId } = filtersFromUrl || {};
    const res = yield call(fetchPlpProducts, {
      start: 0,
      apiConfig,
      rows: 0,
      xappURLConfig,
      categoryPathId,
      skipFacets: false,
      facetsPayload,
    });
    const { response: { numberOfProducts } = {}, facets, stats } = res || {};
    yield call(
      processHplpFacets,
      facets,
      numberOfProducts,
      filtersFromUrl,
      breadCrumbs,
      stats,
      appliedSortId
    );
    if (isHplpCmsSbcEnabled) {
      yield call(getLayoutModels, {}, res, apiConfig);
    }
    return facetsPayload;
  } catch (error) {
    logger.error({
      error: `Error in new plp HorizontalCategorySaga - fetchHorizontalCategoryFacets catch - ${error}`,
      extraData: {
        component: 'HorizontalCategorySaga - fetchHorizontalCategoryFacets',
        payloadRecieved: payload,
      },
    });
    return {};
  }
}

function* isAppliedFiltersMatched(filtersFromUrl) {
  const appliedFilters = yield select(getInitialHplpValues);
  const isHplpFiltersEnabled = yield select(getIsHplpFiltersEnabled);
  let filters = filtersFromUrl;
  if (appliedFilters?.sort === '') {
    filters = { ...filtersFromUrl, sort: '' };
  }
  if (isHplpFiltersEnabled) {
    return isEqual(filters, appliedFilters);
  }
  return true;
}

// eslint-disable-next-line sonarjs/cognitive-complexity, complexity
export function* fetchHorizontalCategoryProducts({ payload }) {
  try {
    const {
      cid,
      selectedGroupId,
      isServer,
      isAlternatePlpEnabled,
      apiConfig: apiConfigFromServer,
      pathName,
      filters,
      isFromFilters,
    } = payload;
    yield put(setHorizontalPlpLoading(true));
    yield put(setIsShopThisStoreHplpLoading(true));
    const breadCrumbs = yield select(computedBreadCrumbs);
    const currentNavIds = breadCrumbs.map((category) => category?.id);
    const xappURLConfig = yield call(getUnbxdXappConfigs);
    const currentCat = breadCrumbs[breadCrumbs?.length - 1];
    const isESpotPromoHplpEnabled = yield select(getIsESpotPromoHplpEnabled);
    const isL3AtL2InHPLPEnabled = yield select(getIsL3AtL2InHPLPEnabled);
    let catFetched = 0;
    let catFetchedWithProducts = 0;
    let availableSubCat = getHlpCatgories(currentCat);
    if (availableSubCat?.length < 1) {
      yield put(setIsProductLoading(false));
      return;
    }
    let facetsPayload;
    if (isShopThisStoreModeOn()) {
      const storeFilter = getStoreFilterData();
      yield put(setLoadedInShopThisStore(true));
      if (selectedGroupId) {
        availableSubCat = getCategoryBasedOnSelectedGroupId(selectedGroupId, availableSubCat);
      }
      availableSubCat = updateShopThisStoreQueryParamUrl(availableSubCat);
      facetsPayload = { filter: storeFilter };
    }
    const initalSubCatToLoad = yield select(getInitialSubCategoryToLoad);
    const productsPerRow = isAlternatePlpEnabled
      ? yield select(getNumberOfProductsPerAPLPRow)
      : yield select(getNumberOfProductsPerHLPRow);
    const parentCategory = currentNavIds[currentNavIds?.length - 1];
    const currentProducts = yield select(getHorizontalPlpProducts);
    const loadedFromShopThisStore = yield select(getIsLoadedInShopThisStore);
    const apiConfig = apiConfigFromServer || getAPIConfig();
    let isFiltersMatched = true;
    if (!loadedFromShopThisStore && pathName) {
      isFiltersMatched = yield isAppliedFiltersMatched(
        getFiltersPayload(pathName, filters).filtersFromUrl
      );
    }
    // invoking facet's api.
    if (!isShopThisStoreModeOn()) {
      facetsPayload =
        getFiltersPayload(pathName, filters) && getFiltersPayload(pathName, filters).facetsPayload;
      const currentCategory = breadCrumbs[breadCrumbs?.length - 1];
      const isL2Category = getIsL2Category(currentCategory, facetsPayload);
      const pathId = isL2Category ? currentNavIds.join('>') : currentCat.id;
      yield fork(fetchHorizontalCategoryFacets, {
        categoryPathId: pathId,
        xappURLConfig,
        productsPerRow,
        apiConfig,
        pathName,
        filters,
        parentCategory,
      });
    }
    if (
      currentProducts?.dataAvailableFor === parentCategory &&
      !loadedFromShopThisStore &&
      !isFromFilters &&
      isFiltersMatched
    ) {
      yield put(setLoadedInShopThisStore(false));
      yield put(setIsProductLoading(false));
      return;
    }
    yield put(clearHorizontalPageSEOData());
    let dataAvailableFor = parentCategory;
    if (isServer) {
      dataAvailableFor = '';
    }
    CURRENT_CATEGORY.set('id', parentCategory);
    while (catFetchedWithProducts < initalSubCatToLoad && catFetched < availableSubCat?.length) {
      const nextId = availableSubCat[catFetched].categoryInfo.id;
      let nextL3Id = '';
      if (availableSubCat[catFetched].hasL3 > 0) {
        nextL3Id = availableSubCat[catFetched]?.subCatInfo?.id;
      }
      const categoryPathMap =
        nextL3Id && isL3AtL2InHPLPEnabled
          ? [...currentNavIds, nextId, nextL3Id]
          : [...currentNavIds, nextId];
      const categoryPathId = categoryPathMap?.join('>');

      const res = yield call(fetchPlpProducts, {
        start: 0,
        apiConfig,
        rows: productsPerRow,
        xappURLConfig,
        categoryPathId,
        skipFacets: true,
        ...(facetsPayload && { facetsPayload }),
      });
      const seoDataPayload = {
        apiConfig,
        page: availableSubCat[catFetched]?.categoryInfo?.seoToken,
        isHorizontalPage: true,
        module: SEO_DATA.categorySeoData,
        catId: availableSubCat[catFetched]?.categoryInfo?.id,
      };
      yield put(loadCategoryPageSeoData(seoDataPayload));
      const { response: { products, numberOfProducts } = {} } = res;

      let eSpotHplpPromoContent = {};
      if (isESpotPromoHplpEnabled && products?.length > 0) {
        const eSpotHplpPromo = yield call(getESpotHplpPromo, {
          categoryInfo: availableSubCat[catFetched].categoryInfo,
        });
        if (!isEmpty(eSpotHplpPromo)) {
          eSpotHplpPromoContent = yield call(fetchModuleDynamic, eSpotHplpPromo);
        }
      }

      let updatedProducts = products;
      if (!isEmpty(eSpotHplpPromoContent)) {
        updatedProducts = [eSpotHplpPromoContent, ...updatedProducts.slice(0, productsPerRow - 1)];
      }

      availableSubCat[catFetched] = {
        ...availableSubCat[catFetched],
        numberOfProducts,
        products: updatedProducts,
      };
      if (products?.length) {
        catFetchedWithProducts += 1;
      }
      catFetched += 1;
    }
    if (catFetched > 0) {
      yield put(
        setHorizontalCategoryProducts({ [parentCategory]: availableSubCat, dataAvailableFor })
      );
      yield put(setIsProductLoading(false));
      yield put(setHorizontalPlpLoading(false));
      yield put(setIsShopThisStoreHplpLoading(false));
      yield put(setHlpCatsFetched(catFetched));
      yield put(setHlpCID(cid));
    } else {
      yield put(setIsProductLoading(false));
      yield put(setHorizontalPlpLoading(false));
    }
  } catch (error) {
    yield put(setIsProductLoading(false));
    yield put(setHorizontalPlpLoading(false));
    // Set error message to redux and raygun
    logger.error({
      error: `Error in new plp HorizontalCategorySaga - fetchHorizontalCategoryProducts catch - ${error}`,
      extraData: {
        component: 'HorizontalCategorySaga - fetchHorizontalCategoryProducts',
        payloadRecieved: payload,
      },
    });
  }
}

export function* fetchInitialHplpData({ payload }) {
  try {
    yield put(setHplpInitialDataLoading(true));

    const { currentNavIds } = payload;
    const xappURLConfig = yield call(getUnbxdXappConfigs);
    const apiConfig = getAPIConfig();
    const isHplpCmsSbcEnabled = yield select(getIsHplpCmsSbcEnabled);
    const hplpLayoutData = yield select(getHplpLayoutData) || {};
    const parentCategoryId = currentNavIds[currentNavIds?.length - 1];
    const categoryPathId = currentNavIds.join('>');

    if (!isHplpCmsSbcEnabled || hplpLayoutData?.parentCategoryId?.fetched) return;

    yield put(
      loadLayoutData(
        { ...hplpLayoutData, [parentCategoryId]: { fetched: false, cid: parentCategoryId } },
        'hplp'
      )
    );

    const res = yield call(fetchPlpProducts, {
      start: 0,
      apiConfig,
      rows: 0,
      xappURLConfig,
      categoryPathId,
      skipFacets: false,
    });
    const bannerJsonData = parseContentData(res?.banner?.banners);
    yield call(fetchHplpModulesAndLayout, bannerJsonData, parentCategoryId, apiConfig, true);
    yield put(setHplpInitialDataLoading(false));
  } catch (error) {
    yield put(setHplpInitialDataLoading(false));
    // Set error message to redux and raygun
    logger.error({
      error: `Error in new plp HorizontalCategorySaga - fetchInitialHplpData catch - ${error}`,
      extraData: {
        component: 'HorizontalCategorySaga - fetchInitialHplpData',
        payloadRecieved: payload,
      },
    });
  }
}

function* getEspotContent({ espotData, categoryPathId }) {
  const eSpotPlpPromo = getESpotPlp(espotData, categoryPathId);
  let espotContent;
  if (!isEmpty(eSpotPlpPromo)) {
    const eSpotPlpPromoContent = yield call(fetchModuleDynamic, eSpotPlpPromo);
    if (!isEmpty(eSpotPlpPromoContent)) {
      espotContent = { type: 'espot', categoryId: categoryPathId, data: eSpotPlpPromoContent };
    }
  }
  return espotContent;
}

function* handleErrorInFetchingHPLPApp({ payload, error }) {
  yield put(setHorizontalPlpLoading(false));
  // Set error message to redux and raygun
  logger.error({
    error: `Error in new plp HorizontalCategorySaga - fetchHorizontalCategoryProductsApp catch - ${error}`,
    extraData: {
      component: 'HorizontalCategorySaga - fetchHorizontalCategoryProductsApp',
      payloadRecieved: payload,
    },
  });
}

function* fetchConfigurations() {
  return {
    xappURLConfig: yield call(getUnbxdXappConfigs),
    apiConfig: getAPIConfig(),
    productsPerRow: yield select(getNumberOfProductsPerHLPRow),
    isL3AtL2InHPLPEnabled: yield select(getIsL3AtL2InHPLPEnabled),
    isESpotPromoHplpEnabled: yield select(getIsESpotPromoHplpEnabled),
    initalSubCatToLoad: yield select(getInitialSubCategoryToLoad),
    currentHplpProducts: yield select(getHorizontalPlpProducts),
  };
}

function* processHPLPResponseApp({
  products,
  subCategory,
  categoryPathId,
  isESpotPromoHplpEnabled,
  productsPerRow,
}) {
  const state = yield select();
  const updatedProducts = products.map((product) => {
    return parseHPLPProductInfo(product, state, {
      isUSStore: true,
      allProducts: products,
    });
  });
  let updatedProductsWithEspot = updatedProducts;
  if (isESpotPromoHplpEnabled) {
    const espotContent = yield call(getEspotContent, {
      espotData: subCategory.espotData,
      categoryPathId,
    });
    if (espotContent) {
      updatedProductsWithEspot = [espotContent, ...updatedProducts.slice(0, productsPerRow - 1)];
    }
  }
  return updatedProductsWithEspot;
}

export function* fetchHorizontalCategoryProductsApp({ payload }) {
  try {
    const { subCategories, currentNavIds } = payload;

    if (!subCategories || subCategories?.length < 1) {
      return;
    }

    yield put(setHorizontalPlpLoading(true));
    const {
      xappURLConfig,
      apiConfig,
      productsPerRow,
      isL3AtL2InHPLPEnabled,
      isESpotPromoHplpEnabled,
      initalSubCatToLoad,
      currentHplpProducts,
    } = yield call(fetchConfigurations);

    const parentCategory = currentNavIds[currentNavIds?.length - 1];
    let catFetched = currentHplpProducts?.[parentCategory]?.catFetched || 0;
    let catFetchedWithProducts = 0;
    const subCategoriesWithProducts = currentHplpProducts?.[parentCategory] || {};

    while (catFetchedWithProducts < initalSubCatToLoad && catFetched < subCategories?.length) {
      const subCategory = subCategories[catFetched];
      const categoryPathMap = [...currentNavIds, subCategory.categoryId];
      if (isL3AtL2InHPLPEnabled && subCategory.hasL3 && subCategory.firstSubCategoryId) {
        categoryPathMap.push(subCategory.firstSubCategoryId);
      }
      const categoryPathId = categoryPathMap?.join('>');

      const res = yield call(fetchPlpProducts, {
        start: 0,
        apiConfig,
        rows: productsPerRow,
        xappURLConfig,
        categoryPathId,
        skipFacets: true,
      });
      const { response: { products, numberOfProducts } = {} } = res;
      if (products && products?.length > 0) {
        const updatedProducts = yield call(processHPLPResponseApp, {
          products,
          isESpotPromoHplpEnabled,
          subCategory,
          categoryPathId,
          productsPerRow,
        });
        subCategoriesWithProducts[subCategory.categoryId] = {
          numberOfProducts,
          products: updatedProducts,
        };
        catFetchedWithProducts += 1;
      }
      catFetched += 1;
    }
    if (catFetched > 0) {
      yield put(
        setHorizontalCategoryProducts({
          ...currentHplpProducts,
          [parentCategory]: {
            ...subCategoriesWithProducts,
            catFetched,
          },
        })
      );
      yield put(setHorizontalPlpLoading(false));
    } else {
      yield put(setHorizontalPlpLoading(false));
    }
  } catch (error) {
    yield call(handleErrorInFetchingHPLPApp, { payload, error });
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function* fetchMoreHorizontalCategory({ payload }) {
  try {
    const isL3AtL2InHPLPEnabled = yield select(getIsL3AtL2InHPLPEnabled);
    const isAlternatePlpEnabled =
      payload && payload.isAlternatePlpEnabled ? payload.isAlternatePlpEnabled : false;
    yield put(setHorizontalPlpLoading(true));
    const breadCrumbs = yield select(computedBreadCrumbs);
    const isHplpFiltersEnabled = yield select(getIsHplpFiltersEnabled);
    const isESpotPromoHplpEnabled = yield select(getIsESpotPromoHplpEnabled);
    const currentNavIds = breadCrumbs.map((category) => category?.id);
    const xappURLConfig = yield call(getUnbxdXappConfigs);
    xappURLConfig.throttleConfig = {
      active: true, // set false to pause queue
      rate: 9, // how many requests can be sent every `ratePer`
      ratePer: 3000, // number of ms in which `rate` requests may be sent
      concurrent: 6, // how many requests can be sent concurrently
    };
    const subcatCategoryMap = yield select(getHorizontalPlpProducts);
    const parentCategory = currentNavIds[currentNavIds?.length - 1];
    const availableSubCat = subcatCategoryMap[parentCategory];
    const categoryToLoadOnScroll = yield select(getSubCategoryToLoadOnScroll);
    const productsPerRow = isAlternatePlpEnabled
      ? yield select(getNumberOfProductsPerAPLPRow)
      : yield select(getNumberOfProductsPerHLPRow);
    const currentIndex =
      availableSubCat?.filter((cat) => typeof cat.numberOfProducts !== 'undefined')?.length || 0;
    const maxIndex = currentIndex + categoryToLoadOnScroll;
    yield put(setHorizontalPlpLoading(true));
    // generator function is not expected to return
    // eslint-disable-next-line func-names, consistent-return
    const fetchCategory = function* (index) {
      if (index >= availableSubCat?.length) return;
      const nextId = availableSubCat[index].categoryInfo.id;
      let nextL3Id = '';
      if (availableSubCat[index].hasL3 > 0) {
        nextL3Id = availableSubCat[index]?.subCatInfo?.id;
      }
      const categoryPathMap =
        nextL3Id && isL3AtL2InHPLPEnabled
          ? [...currentNavIds, nextId, nextL3Id]
          : [...currentNavIds, nextId];
      const categoryPathId = categoryPathMap?.join('>');
      let facetsPayload;
      if (isHplpFiltersEnabled) {
        const filters = yield select(getInitialHplpValues);
        facetsPayload = isEmpty(filters) ? {} : extractFilters(filters);
        if (filters?.sort) {
          facetsPayload.sort = filters.sort;
        }
      }
      if (isShopThisStoreModeOn()) {
        const storeFilter = getStoreFilterData();

        facetsPayload = { filter: storeFilter };
      }
      const res = yield call(fetchPlpProducts, {
        start: 0,
        rows: productsPerRow,
        xappURLConfig,
        categoryPathId,
        skipFacets: true,
        ...(facetsPayload && { facetsPayload }),
      });
      const apiConfig = getAPIConfig();
      const seoDataPayload = {
        apiConfig,
        page: availableSubCat[index].categoryInfo?.seoToken,
        isHorizontalPage: true,
        module: SEO_DATA.categorySeoData,
        catId: availableSubCat[index].categoryInfo.id,
      };
      yield put(loadCategoryPageSeoData(seoDataPayload));
      const { response: { products, numberOfProducts } = {} } = res;

      let eSpotHplpPromoContent = {};
      if (isESpotPromoHplpEnabled && products?.length > 0) {
        const eSpotHplpPromo = yield call(getESpotHplpPromo, {
          categoryInfo: availableSubCat[index].categoryInfo,
        });
        if (!isEmpty(eSpotHplpPromo)) {
          eSpotHplpPromoContent = yield call(fetchModuleDynamic, eSpotHplpPromo);
        }
      }

      let updatedProducts = products;
      if (!isEmpty(eSpotHplpPromoContent)) {
        updatedProducts = [eSpotHplpPromoContent, ...updatedProducts.slice(0, productsPerRow - 1)];
      }
      if (parentCategory === CURRENT_CATEGORY.get('id')) {
        availableSubCat[index] = {
          ...availableSubCat[index],
          numberOfProducts,
          products: updatedProducts,
        };
      }
    };
    const pendingPromises = [];
    for (let i = currentIndex; i <= maxIndex; i += 1) {
      pendingPromises.push(call(fetchCategory, i));
    }
    yield all(pendingPromises);
    subcatCategoryMap[parentCategory] = availableSubCat;
    yield put(setHorizontalCategoryProducts(subcatCategoryMap));
    yield put(setHlpCatsFetched(maxIndex));
    yield put(setIsProductLoading(false));
    yield put(setHorizontalPlpLoading(false));
  } catch (error) {
    yield put(setIsProductLoading(false));
    // Set error message to redux and raygun
    logger.error({
      error: `Error in new plp HorizontalCategorySaga - fetchMoreHorizontalCategory catch - ${error}`,
      extraData: {
        component: 'HorizontalCategorySaga - fetchMoreHorizontalCategory',
        payloadRecieved: payload,
      },
    });
  }
}

function* HorizontalCategorySaga() {
  yield takeLatest(
    HORIZONTAL_PLP_CONSTANTS.FETCH_HORIZONTAL_CATEGORY_PRODUCTS,
    fetchHorizontalCategoryProducts
  );

  yield debounce(
    250,
    HORIZONTAL_PLP_CONSTANTS.FETCH_MORE_HORIZONTAL_CATEGORY_PRODUCTS,
    fetchMoreHorizontalCategory
  );

  yield takeLatest(
    HORIZONTAL_PLP_CONSTANTS.FETCH_HORIZONTAL_CATEGORY_PRODUCTS_APP,
    fetchHorizontalCategoryProductsApp
  );

  yield takeLatest(HORIZONTAL_PLP_CONSTANTS.FETCH_INITIAL_HPLP_DATA, fetchInitialHplpData);
}

export default HorizontalCategorySaga;
